import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AdminSidebar from '@/pages/admin/components/AdminSidebar';
import AdminTopBar from '@/pages/admin/components/AdminTopBar';
import AppointmentsFilters from './components/AppointmentsFilters';
import AppointmentsTable from './components/AppointmentsTable';
import AppointmentsCalendar from './components/AppointmentsCalendar';
import PageHeader from '@/components/feature/PageHeader';
import { useAppointments } from '@/hooks/useAppointments';
import { theme } from '@/lib/theme';

type DisplayMode = 'table' | 'calendar';

export default function AppointmentsPage() {
  const [displayMode, setDisplayMode] = useState<DisplayMode>('table');

  const {
    appointments,
    loading,
    error,
    filters,
    cancelling,
    completing,
    updateFilters,
    clearFilters,
    refetch,
    cancelAppointment,
    markAsNoShow,
    confirmManually,
    completeService,
  } = useAppointments();

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: theme.colors.bgBase }}>
      <AdminSidebar />

      <div className="flex-1 flex flex-col min-w-0">
        <AdminTopBar
          title="Citas"
          subtitle="Gestiona y supervisa todas las reservas"
        />

        <main className="flex-1 overflow-y-auto px-6 py-8">
          <PageHeader
            title="Gestión de citas"
            subtitle="Visualiza, filtra y cancela reservas"
            action={{
              label: 'Actualizar',
              icon: 'ri-refresh-line',
              onClick: refetch,
              loading,
            }}
          />

          {/* Error banner */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-3 rounded-xl px-5 py-4 mb-5 border"
              style={{
                backgroundColor: theme.colors.dangerMuted,
                borderColor: 'rgba(248,113,113,0.2)',
              }}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-red-400 text-base" />
              </div>
              <p className="text-red-400 text-sm flex-1">{error}</p>
              <button
                onClick={refetch}
                className="text-red-400 hover:text-red-300 text-xs underline cursor-pointer whitespace-nowrap"
              >
                Reintentar
              </button>
            </motion.div>
          )}

          {/* View mode toggle + filters row */}
          <div className="flex items-start gap-3 mb-5 flex-wrap">
            {/* Display mode toggle */}
            <div
              className="flex items-center p-1 rounded-lg flex-shrink-0"
              style={{ backgroundColor: theme.colors.bgSurface, border: `1px solid ${theme.colors.border}` }}
            >
              <button
                onClick={() => setDisplayMode('table')}
                className="relative flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
                style={{ color: displayMode === 'table' ? theme.colors.primary : 'rgba(255,255,255,0.4)' }}
              >
                {displayMode === 'table' && (
                  <motion.div
                    layoutId="display-mode-indicator"
                    className="absolute inset-0 rounded-md"
                    style={{ backgroundColor: theme.colors.primaryMuted, border: `1px solid ${theme.colors.primaryBorder}` }}
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
                <div className="w-4 h-4 flex items-center justify-center relative z-10">
                  <i className="ri-table-line text-sm" />
                </div>
                <span className="relative z-10">Tabla</span>
              </button>

              <button
                onClick={() => setDisplayMode('calendar')}
                className="relative flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
                style={{ color: displayMode === 'calendar' ? theme.colors.primary : 'rgba(255,255,255,0.4)' }}
              >
                {displayMode === 'calendar' && (
                  <motion.div
                    layoutId="display-mode-indicator"
                    className="absolute inset-0 rounded-md"
                    style={{ backgroundColor: theme.colors.primaryMuted, border: `1px solid ${theme.colors.primaryBorder}` }}
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
                <div className="w-4 h-4 flex items-center justify-center relative z-10">
                  <i className="ri-calendar-line text-sm" />
                </div>
                <span className="relative z-10">Calendario</span>
              </button>
            </div>

            {/* Filters — only in table mode */}
            {displayMode === 'table' && (
              <motion.div
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -8 }}
                className="flex-1 min-w-0"
              >
                <AppointmentsFilters
                  filters={filters}
                  onUpdate={updateFilters}
                  onClear={clearFilters}
                  total={appointments.length}
                />
              </motion.div>
            )}

            {/* Calendar info badge */}
            {displayMode === 'calendar' && (
              <motion.div
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border"
                style={{
                  backgroundColor: theme.colors.bgSurface,
                  borderColor: theme.colors.border,
                }}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-information-line text-sm text-white/30" />
                </div>
                <p className="text-white/40 text-xs">
                  Mostrando {appointments.length} cita{appointments.length !== 1 ? 's' : ''} — haz clic en cualquier slot para ver el detalle
                </p>
              </motion.div>
            )}
          </div>

          {/* Content */}
          <AnimatePresence mode="wait">
            {displayMode === 'table' ? (
              <motion.div
                key="table"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
              >
                <AppointmentsTable
                  appointments={appointments}
                  loading={loading}
                  cancelling={cancelling}
                  completing={completing}
                  onCancel={cancelAppointment}
                  onNoShow={markAsNoShow}
                  onComplete={completeService}
                  onConfirm={confirmManually}
                />
              </motion.div>
            ) : (
              <motion.div
                key="calendar"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
              >
                <AppointmentsCalendar
                  appointments={appointments}
                  loading={loading}
                  cancelling={cancelling}
                  completing={completing}
                  onCancel={cancelAppointment}
                  onNoShow={markAsNoShow}
                  onComplete={completeService}
                  onConfirm={confirmManually}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
